/** @jsxImportSource @emotion/react */
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { Table } from "../../../components/table-components/Table";
import { TableBody } from "../../../components/table-components/TableBody";
import { TableCellHeaderText } from "../../../components/table-components/TableCellHeaderText";
import { TableCellText } from "../../../components/table-components/TableCellText";
import { TableRow } from "../../../components/table-components/TableRow";
import { TableRowHeader } from "../../../components/table-components/TableRowHeader";
import { TableEllipsisDropdown } from "../../../components/TableEllipsisDropdown";
import { firstTableCellStyle } from "../../../style-util";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useSearchParamsManipulation } from "../../../hooks/use-searchparams-manipulation";
import { SmallBadge } from "../../../components/SmallBadge";
import { css } from "@emotion/react";
import { IconButton } from "../../../components/IconButton";
import { useState } from "react";
import { toastr } from "react-redux-toastr";
import { DeliveryProviderConfiguration } from "../models/delivery-provider-configuration";
import { MutateDeliveryProviderConfigurationDrawer } from "./MutateDeliveryProviderConfigurationDrawer";
import { useDeleteDeliveryProviderConfiguration } from "../service/delivery-provider-configrations-api";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { getErrorMessage } from "../../../error-handler";

interface Props {
  deliveryProviderConfigurations: DeliveryProviderConfiguration[];
}

const badgeContainerStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: row;
`;

const additionalBadgeStyles = css`
  overflow: hidden;
  width: max-content;
  max-width: 200px;
`;

const textStyles = css`
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DeliveryProvidersConfigurationsTable = ({
  deliveryProviderConfigurations,
}: Props) => {
  const [hasCopiedId, setHasCopiedId] = useState("");
  const { option, setOption, removeOption } = useSearchParamsManipulation();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const {
    mutateAsync: deleteDeliveryProviderConfiguration,
    isLoading: isDeletingDeliveryProviderConfiguration,
  } = useDeleteDeliveryProviderConfiguration();

  return (
    <>
      <Table>
        <thead>
          <TableRowHeader>
            <TableCellHeaderText css={firstTableCellStyle}>
              Name
            </TableCellHeaderText>
            <TableCellHeaderText>Provider</TableCellHeaderText>
            <TableCellHeaderText>Api key</TableCellHeaderText>
            <TableCellHeaderText>Base Url</TableCellHeaderText>
            <TableCellHeaderText>Client id</TableCellHeaderText>
            <TableCellHeaderText>Enabled</TableCellHeaderText>
            <th />
          </TableRowHeader>
        </thead>
        <TableBody>
          {deliveryProviderConfigurations.map(
            (deliveryProviderConfiguration, i) => (
              <TableRow key={i}>
                <TableCellText
                  css={firstTableCellStyle}
                  onClick={() => {
                    setOption(deliveryProviderConfiguration.id);
                  }}
                >
                  <StyledText
                    className="bodyTextRegular mb-0"
                    color={colors.textPrimary}
                  >
                    {deliveryProviderConfiguration.name || "-"}
                  </StyledText>
                </TableCellText>
                <TableCellText
                  onClick={() => {
                    setOption(deliveryProviderConfiguration.id);
                  }}
                >
                  <StyledText
                    className="bodyTextRegular mb-0"
                    color={colors.textPrimary}
                  >
                    {deliveryProviderConfiguration.provider || "-"}
                  </StyledText>
                </TableCellText>
                <TableCellText
                  onClick={() => {
                    if (deliveryProviderConfiguration.apiKey) {
                      return;
                    }
                    setOption(deliveryProviderConfiguration.id);
                  }}
                >
                  {deliveryProviderConfiguration.apiKey ? (
                    <div css={badgeContainerStyles}>
                      <SmallBadge
                        additionalStyles={additionalBadgeStyles}
                        onClick={() => {
                          setOption(deliveryProviderConfiguration.id);
                        }}
                      >
                        <StyledText
                          className="bodyTextRegular mb-0"
                          color={colors.textPrimary}
                          css={textStyles}
                        >
                          {deliveryProviderConfiguration.apiKey}
                        </StyledText>
                      </SmallBadge>
                      <IconButton
                        icon={
                          hasCopiedId === deliveryProviderConfiguration.id ? (
                            <FontAwesomeIcon icon={solid("check")} />
                          ) : (
                            <FontAwesomeIcon icon={solid("copy")} />
                          )
                        }
                        disabled={
                          hasCopiedId === deliveryProviderConfiguration.id
                        }
                        onClick={() => {
                          navigator.clipboard.writeText(
                            deliveryProviderConfiguration.apiKey!
                          );
                          setHasCopiedId(deliveryProviderConfiguration.id);
                          toastr.info("Success", "Copied to clipboard");
                          setTimeout(() => {
                            setHasCopiedId("");
                          }, 2000);
                        }}
                      />
                    </div>
                  ) : (
                    <StyledText
                      className="bodyTextRegular mb-0"
                      color={colors.textPrimary}
                    >
                      -
                    </StyledText>
                  )}
                </TableCellText>
                <TableCellText
                  onClick={() => {
                    setOption(deliveryProviderConfiguration.id);
                  }}
                >
                  <StyledText
                    className="bodyTextRegular mb-0"
                    color={colors.textPrimary}
                  >
                    {deliveryProviderConfiguration.baseUrl || "-"}
                  </StyledText>
                </TableCellText>
                <TableCellText
                  onClick={() => {
                    setOption(deliveryProviderConfiguration.id);
                  }}
                >
                  <StyledText
                    className="bodyTextRegular mb-0"
                    color={colors.textPrimary}
                  >
                    {deliveryProviderConfiguration.clientId || "-"}
                  </StyledText>
                </TableCellText>
                <TableCellText
                  onClick={() => {
                    setOption(deliveryProviderConfiguration.id);
                  }}
                >
                  <StyledText
                    className="bodyTextRegular mb-0"
                    color={colors.textPrimary}
                  >
                    {deliveryProviderConfiguration.isEnabled ? "Yes" : "No"}
                  </StyledText>
                </TableCellText>
                <TableEllipsisDropdown
                  menu={{
                    items: [
                      {
                        key: "1",
                        label: (
                          <DropdownItemButton
                            onClick={() => {
                              setOption(deliveryProviderConfiguration.id);
                            }}
                            type="button"
                            icon={<FontAwesomeIcon icon={solid("pen")} />}
                          >
                            Edit
                          </DropdownItemButton>
                        ),
                      },
                      {
                        key: "2",
                        label: (
                          <DropdownItemButton
                            onClick={async () => {
                              try {
                                await deleteDeliveryProviderConfiguration({
                                  organizationKeyName,
                                  data: {
                                    id: deliveryProviderConfiguration.id,
                                  },
                                });
                                toastr.success(
                                  "Success",
                                  "Delivery configuration deleted"
                                );
                              } catch (error: unknown) {
                                const errorMessage = getErrorMessage(error);
                                toastr.error("Error", errorMessage);
                              }
                            }}
                            type="button"
                            icon={
                              isDeletingDeliveryProviderConfiguration ? (
                                <ActionSpinner />
                              ) : (
                                <FontAwesomeIcon icon={solid("trash")} />
                              )
                            }
                            disabled={isDeletingDeliveryProviderConfiguration}
                          >
                            Delete
                          </DropdownItemButton>
                        ),
                      },
                    ],
                  }}
                />
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
      <MutateDeliveryProviderConfigurationDrawer
        isOpen={!!option}
        onClose={() => {
          removeOption();
        }}
        deliveryProviderConfiguration={deliveryProviderConfigurations.find(
          (deliveryProviderConfiguration) =>
            deliveryProviderConfiguration.id === option
        )}
      />
    </>
  );
};
